import { useQuery } from '@tanstack/react-query';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayDate, formatDisplayTime } from '@hq/utils';
import { Alert, Card, Col, Progress, Row, Statistic, Table, Tag, Typography } from 'antd';
import { hq } from '../session';

const STATUS_META: Record<string, { color: string; label: string }> = {
  RUNNING: { color: 'green', label: '运行中' },
  INIT: { color: 'blue', label: '待开通' },
  SUSPEND: { color: 'orange', label: '已停用' },
  EXPIRED: { color: 'red', label: '已到期' }
};

/** 字节转可读容量（总览只展示用量水位，精确值交给租户详情）。 */
const formatBytes = (bytes: number): string => {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** exp).toFixed(exp === 0 ? 0 : 1)} ${units[exp]}`;
};

/** 平台总览：租户状态分布 + 纳管规模（`GET /api/sys/platform/overview`，SYSTEM 门户）。 */
export default function PlatformOverview() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['platform', 'overview'],
    queryFn: () => hq.tenant.overview()
  });

  const tenants = data?.tenants;

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        平台总览
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        跨租户视角仅平台运营端可见；租户维度的业务数据需在对应门户查看。
      </Typography.Paragraph>

      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error instanceof ApiError ? error.friendlyMessage : String(error)}
          description="若提示无权访问，请使用 SYSTEM 门户账号（如 t_platform）登录。"
        />
      ) : null}

      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="租户总数" value={tenants?.total ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="运行中" value={tenants?.running ?? 0} valueStyle={{ color: '#389e0d' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="已停用 / 到期" value={(tenants?.suspended ?? 0) + (tenants?.expired ?? 0)} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="待开通" value={tenants?.init ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="纳管机构" value={data?.orgCount ?? 0} suffix="家" />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="监管单位" value={data?.govCount ?? 0} suffix="家" />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="运营中心" value={data?.centerCount ?? 0} suffix="个" />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="租户内账号" value={data?.userCount ?? 0} suffix="个" />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card title="近 30 天单量" loading={isLoading} size="small">
            <Row gutter={[16, 16]}>
              <Col xs={12} md={6}>
                <Statistic title="申领" value={data?.orders.apply ?? 0} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="配送" value={data?.orders.delivery ?? 0} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="入库" value={data?.orders.inbound ?? 0} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="出库" value={data?.orders.outbound ?? 0} />
              </Col>
            </Row>
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
              合计 {data?.orders.total ?? 0} 张单据（跨全部租户）
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="存储用量" loading={isLoading} size="small">
            <Progress
              percent={Math.min(100, Math.round((data?.storage.usageRatio ?? 0) * 100))}
              status={(data?.storage.usageRatio ?? 0) >= 0.8 ? 'exception' : 'normal'}
            />
            <Typography.Text type="secondary">
              已用 {formatBytes(data?.storage.usedBytes ?? 0)} / 配额 {formatBytes(data?.storage.quotaBytes ?? 0)}
            </Typography.Text>
          </Card>
        </Col>
      </Row>

      <Card
        style={{ marginTop: 16 }}
        title="需要关注的租户"
        extra={<Typography.Text type="secondary">非运行中或 7 天内到期</Typography.Text>}
        loading={isLoading}
        size="small"
      >
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={data?.attention ?? []}
          locale={{ emptyText: '暂无异常租户' }}
          columns={[
            { title: '租户编码', dataIndex: 'tenantCode', width: 160 },
            { title: '租户名称', dataIndex: 'tenantName' },
            {
              title: '状态',
              dataIndex: 'status',
              width: 110,
              render: (status: string) => {
                const meta = STATUS_META[status] ?? { color: 'default', label: status };
                return <Tag color={meta.color}>{meta.label}</Tag>;
              }
            },
            { title: '到期日', dataIndex: 'serviceEnd', width: 130, render: (v: string | null) => formatDisplayDate(v) },
            {
              title: '原因',
              dataIndex: 'reason',
              width: 140,
              render: (v: string) => <Tag color="volcano">{v}</Tag>
            }
          ]}
        />
      </Card>

      <Card style={{ marginTop: 16 }} title="近 7 天新开租户" loading={isLoading}>
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={data?.recent ?? []}
          locale={{ emptyText: '近 7 天没有新开租户' }}
          columns={[
            { title: '租户编码', dataIndex: 'tenantCode', width: 160 },
            { title: '租户名称', dataIndex: 'tenantName' },
            {
              title: '状态',
              dataIndex: 'status',
              width: 110,
              render: (status: string) => {
                const meta = STATUS_META[status] ?? { color: 'default', label: status };
                return <Tag color={meta.color}>{meta.label}</Tag>;
              }
            },
            { title: '开通时间', dataIndex: 'createAt', width: 180, render: (v: string | null) => formatDisplayTime(v) }
          ]}
        />
      </Card>
    </div>
  );
}
